import React, { useState } from "react";
import Papa from "papaparse";
import {
  getAuthenticatedAxios,
  buildApiPath,
} from "../../utils/api";

const BatchUserImport = ({ onImportComplete }) => {
  const [importing, setImporting] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [parseErrors, setParseErrors] = useState([]);
  const [importResults, setImportResults] = useState(null);
  const [sendEmails, setSendEmails] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Reset states
    setCsvData([]);
    setParseErrors([]);
    setImportResults(null);

    // Parse CSV
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const errors = [];
        const validUsers = [];

        // Validate each row
        results.data.forEach((row, index) => {
          const rowNum = index + 2; // Account for header row

          // Required fields
          if (!row["Email Address"] || !row["Email Address"].trim()) {
            errors.push(`Row ${rowNum}: Email Address is required`);
            return;
          }
          if (!row["Full Name"] || !row["Full Name"].trim()) {
            errors.push(`Row ${rowNum}: Full Name is required`);
            return;
          }
          if (!row["Password"] || !row["Password"].trim()) {
            errors.push(`Row ${rowNum}: Password is required`);
            return;
          }
          if (!row["Company"] || !row["Company"].trim()) {
            errors.push(`Row ${rowNum}: Company is required`);
            return;
          }

          // Validate email format
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(row["Email Address"])) {
            errors.push(`Row ${rowNum}: Invalid email format`);
            return;
          }

          // Note: Password validation is bypassed for batch imports to allow simpler demo passwords

          // Validate role
          const role = (row["Role"] || "user").toLowerCase();
          if (!["admin", "user", "demo"].includes(role)) {
            errors.push(
              `Row ${rowNum}: Role must be 'admin', 'user', or 'demo'`,
            );
            return;
          }

          // Parse search limit
          let searchLimit = null;
          if (row["Monthly Search Limit"]) {
            searchLimit = parseInt(row["Monthly Search Limit"]);
            if (isNaN(searchLimit) || searchLimit < 0) {
              errors.push(
                `Row ${rowNum}: Monthly Search Limit must be a positive number`,
              );
              return;
            }
          }

          // Add valid user
          validUsers.push({
            email: row["Email Address"].trim().toLowerCase(),
            full_name: row["Full Name"].trim(),
            password: row["Password"],
            company: row["Company"].trim(),
            role: role,
            monthly_search_limit: searchLimit,
          });
        });

        setParseErrors(errors);
        setCsvData(validUsers);
        if (validUsers.length > 0) {
          setShowPreview(true);
        }
      },
      error: (error) => {
        setParseErrors([`Failed to parse CSV: ${error.message}`]);
      },
    });
  };

  const handleImport = async () => {
    if (csvData.length === 0) {
      alert("No valid users to import");
      return;
    }

    setImporting(true);
    setImportResults(null);

    try {
      const axios = getAuthenticatedAxios();
      const response = await axios.post(
        buildApiPath("/admin/users/batch-import"),
        {
          users: csvData,
          send_welcome_emails: sendEmails,
        },
      );

      setImportResults(response.data);

      // Clear the data after successful import
      setCsvData([]);
      setShowPreview(false);

      // Reset file input
      const fileInput = document.getElementById("csv-file-input");
      if (fileInput) {
        fileInput.value = "";
      }

      // Call parent callback if provided
      if (onImportComplete) {
        onImportComplete(response.data);
      }
    } catch (error) {
      console.error("Import error:", error);
      console.error("Error response data:", error.response?.data);
      let errorMessage = "Failed to import users";

      if (error.response?.data?.detail) {
        // Handle different error formats from FastAPI
        const detail = error.response.data.detail;
        if (typeof detail === "string") {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Validation errors array
          errorMessage = detail
            .map((err) =>
              typeof err === "string" ? err : err.msg || JSON.stringify(err),
            )
            .join(", ");
        } else if (typeof detail === "object") {
          // Single validation error object
          errorMessage = detail.msg || detail.message || JSON.stringify(detail);
        }
      }

      setImportResults({
        error: true,
        message: errorMessage,
      });
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const template = [
      [
        "Email Address",
        "Full Name",
        "Password",
        "Company",
        "Role",
        "Monthly Search Limit",
      ],
      [
        "demo1@medilens.ai",
        "Demo User One",
        "DemoPass123!",
        "MediLens Demo",
        "user",
        "100",
      ],
      [
        "demo2@medilens.ai",
        "Demo Admin",
        "AdminPass123!",
        "MediLens Demo",
        "admin",
        "1000",
      ],
    ];

    const csv = Papa.unparse(template);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "user_import_template.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold mb-6">Batch Import Users</h2>

      {/* Instructions */}
      <div className="mb-6 p-4 bg-blue-50 rounded-lg">
        <h3 className="font-semibold text-blue-900 mb-2">Instructions:</h3>
        <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
          <li>Download the CSV template</li>
          <li>
            Fill in user details (Email, Full Name, Password, Company, Role,
            Monthly Search Limit)
          </li>
          <li>Upload the completed CSV file</li>
          <li>Review the preview and click Import</li>
        </ol>
        <p className="mt-2 text-sm text-blue-700">
          <strong>Notes:</strong>
          <br />
          • Companies will be created automatically if they don't exist
          <br />
          • Role can be: admin, user, or demo
          <br />• Password validation is bypassed for batch imports (allows
          simple passwords)
        </p>
      </div>

      {/* Template Download */}
      <div className="mb-6">
        <button
          onClick={downloadTemplate}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition"
        >
          Download CSV Template
        </button>
      </div>

      {/* File Upload */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Upload CSV File
        </label>
        <input
          id="csv-file-input"
          type="file"
          accept=".csv"
          onChange={handleFileUpload}
          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
        />
      </div>

      {/* Parse Errors */}
      {parseErrors.length > 0 && (
        <div className="mb-6 p-4 bg-red-50 rounded-lg">
          <h3 className="font-semibold text-red-900 mb-2">
            Validation Errors:
          </h3>
          <ul className="list-disc list-inside space-y-1 text-sm text-red-700">
            {parseErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Preview Table */}
      {showPreview && csvData.length > 0 && (
        <div className="mb-6">
          <h3 className="font-semibold mb-2">
            Preview ({csvData.length} users)
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border px-4 py-2 text-left">Email</th>
                  <th className="border px-4 py-2 text-left">Full Name</th>
                  <th className="border px-4 py-2 text-left">Company</th>
                  <th className="border px-4 py-2 text-left">Role</th>
                  <th className="border px-4 py-2 text-left">Search Limit</th>
                </tr>
              </thead>
              <tbody>
                {csvData.slice(0, 10).map((user, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="border px-4 py-2">{user.email}</td>
                    <td className="border px-4 py-2">{user.full_name}</td>
                    <td className="border px-4 py-2">{user.company}</td>
                    <td className="border px-4 py-2">
                      <span
                        className={`px-2 py-1 rounded text-xs ${
                          user.role === "admin"
                            ? "bg-purple-100 text-purple-800"
                            : user.role === "demo"
                              ? "bg-amber-100 text-amber-800"
                              : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {user.role}
                      </span>
                    </td>
                    <td className="border px-4 py-2">
                      {user.monthly_search_limit || "Default"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {csvData.length > 10 && (
              <p className="text-sm text-gray-500 mt-2">
                ... and {csvData.length - 10} more users
              </p>
            )}
          </div>

          {/* Email Option */}
          <div className="mt-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={sendEmails}
                onChange={(e) => setSendEmails(e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm">Send welcome emails to new users</span>
            </label>
          </div>

          {/* Import Button */}
          <div className="mt-4">
            <button
              onClick={handleImport}
              disabled={importing}
              className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {importing ? "Importing..." : `Import ${csvData.length} Users`}
            </button>
          </div>
        </div>
      )}

      {/* Import Results */}
      {importResults && (
        <div
          className={`mt-6 p-4 rounded-lg ${importResults.error ? "bg-red-50" : "bg-green-50"}`}
        >
          {importResults.error ? (
            <div>
              <h3 className="font-semibold text-red-900 mb-2">Import Failed</h3>
              <p className="text-red-700">{importResults.message}</p>
            </div>
          ) : (
            <div>
              <h3 className="font-semibold text-green-900 mb-2">
                Import Complete
              </h3>
              <div className="space-y-2 text-sm">
                <p className="text-green-700">
                  Total: {importResults.total} | Successful:{" "}
                  {importResults.successful} | Failed: {importResults.failed}
                </p>

                {importResults.results && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-green-700 hover:text-green-900">
                      View Details
                    </summary>
                    <div className="mt-2 max-h-60 overflow-y-auto">
                      {importResults.results.map((result, index) => (
                        <div
                          key={index}
                          className={`p-2 mb-1 rounded ${
                            result.success ? "bg-green-100" : "bg-red-100"
                          }`}
                        >
                          <span className="font-medium">{result.email}:</span>{" "}
                          {result.message}
                          {result.company_created && (
                            <span className="ml-2 text-xs text-gray-600">
                              (New company created)
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BatchUserImport;
